import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const CellPopulationDoublingCalculator = () => {
  const [populationData, setPopulationData] = useState({
    initialCount: '',
    finalCount: '',
    timePeriod: '',
    timeUnit: 'hours'
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPopulationData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateDoublingTime = () => {
    const { initialCount, finalCount, timePeriod, timeUnit } = populationData;
    
    const N0 = parseFloat(initialCount);
    const N = parseFloat(finalCount);
    const t = parseFloat(timePeriod);
    
    if (!N0 || !N || !t) {
      setError('Please enter valid numbers for all fields');
      return;
    }

    if (N0 <= 0 || N <= 0) {
      setError('Cell counts must be positive numbers');
      return;
    }

    const doublings = Math.log2(N / N0);
    const doublingTime = t / doublings;

    const timePoints = Array.from({ length: 21 }, (_, i) => (i * t) / 20);
    const populationPoints = timePoints.map(time => (
      N0 * Math.pow(2, time / doublingTime)
    ));

    const calculationSteps = [
      'Step 1: Calculate number of doublings',
      `Number of doublings = log2(Final count / Initial count)`,
      `Number of doublings = log2(${N} / ${N0})`,
      `Number of doublings = ${doublings.toFixed(4)}`,
      '\nStep 2: Calculate doubling time',
      `Doubling time = Total time / Number of doublings`,
      `Doubling time = ${t} ${timeUnit} / ${doublings.toFixed(4)}`,
      `Doubling time = ${doublingTime.toFixed(4)} ${timeUnit}`,
      '\nStep 3: Calculate growth rate',
      `Growth rate (k) = ln(2) / doubling time`,
      `Growth rate = ${(Math.LN2 / doublingTime).toFixed(4)} per ${timeUnit}`
    ];

    const newResults = {
      doublingTime: doublingTime.toFixed(4),
      growthRate: (Math.LN2 / doublingTime).toFixed(4),
      numberOfDoublings: doublings.toFixed(4),
      timeUnit,
      calculationSteps
    };

    setResults(newResults);

    setChartData({
      labels: timePoints.map(t => t.toFixed(2)),
      datasets: [{
        label: 'Cell Population Growth',
        data: populationPoints,
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.4
      }]
    });
  };

  const exampleData = {
    initialCount: 1000,
    finalCount: 8000,
    timePeriod: 24,
    timeUnit: 'hours',
    doublingTime: 8,
    growthRate: 0.0866,
    doublings: 3
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-cyan-200 to-blue-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Cell Population Doubling Time Calculator | Cell Growth Analysis Tool</title>
          <meta name="description" content="Calculate cell population doubling time and growth rate with our advanced calculator. Analyze exponential growth patterns in cell cultures for research and laboratory work." />
          <meta name="keywords" content="cell doubling time calculator, cell growth rate, population doubling, cell culture analysis, biology calculator" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-green-600 mr-4">🦠</div>
            <h1 className="text-3xl font-bold text-gray-800">Cell Population Doubling Time Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate the doubling time and growth rate of a cell population based on initial count, final count, and time period. This calculator helps analyze exponential growth patterns in cell cultures.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Initial Cell Count:</label>
              <input
                type="number"
                name="initialCount"
                value={populationData.initialCount}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter initial cell count"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Final Cell Count:</label>
              <input
                type="number"
                name="finalCount"
                value={populationData.finalCount}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter final cell count"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Time Period:</label>
              <div className="flex items-center gap-4">
                <input
                  type="number"
                  name="timePeriod"
                  value={populationData.timePeriod}
                  onChange={handleInputChange}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Enter time period"
                />
                <select
                  name="timeUnit"
                  value={populationData.timeUnit}
                  onChange={handleInputChange}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>
            </div>
          </div>

          <button
            onClick={calculateDoublingTime}
            className="w-full py-3 px-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Doubling Time</h3>
                  <p className="text-xl font-bold">{results.doublingTime} {results.timeUnit}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Growth Rate</h3>
                  <p className="text-xl font-bold">{results.growthRate} per {results.timeUnit}</p>
                </div>
              </div>

              {chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Line
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Cell Count'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: `Time (${results.timeUnit})`
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Cell Population Growth Curve'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation using sample cell population data:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Initial Cell Count: {exampleData.initialCount} cells</li>
                <li>Final Cell Count: {exampleData.finalCount} cells</li>
                <li>Time Period: {exampleData.timePeriod} {exampleData.timeUnit}</li>
              </ul>

              <h4 className="mb-4">Results:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Doubling Time: {exampleData.doublingTime} {exampleData.timeUnit}</li>
                <li>Growth Rate: {exampleData.growthRate} per {exampleData.timeUnit}</li>
                <li>Number of Doublings: {exampleData.doublings}</li>
              </ul>

              <p><strong>Interpretation:</strong> In this example, the cell population doubles every {exampleData.doublingTime} hours, 
              completing {exampleData.doublings} doublings over the {exampleData.timePeriod}-hour period.</p>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use Cell Population Doubling Calculator <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Cell Population Doubling Calculator effectively:</p>
            
            <div className="step-section mt-4">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Prepare Your Cell Growth Data</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Collect cell counts at different time points</li>
                <li>Record the exact time of each measurement</li>
                <li>Ensure consistent cell counting method</li>
                <li>Note any changes in growth conditions</li>
              </ul>
            </div>

            <div className="step-section mt-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Enter Growth Data</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Input the initial cell count</li>
                <li>Enter the final cell count</li>
                <li>Specify the time duration between counts</li>
                <li>Select the appropriate growth phase</li>
                <li>Click the "Calculate Doubling Time" button</li>
              </ul>
            </div>

            <div className="step-section mt-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Analyze Results</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Review the calculated doubling time</li>
                <li>Check the growth rate</li>
                <li>Verify the number of doublings</li>
                <li>Use the growth curve for visual analysis</li>
                <li>Compare results with standard growth rates</li>
              </ul>
            </div>

            <div className="step-section mt-6 bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Important Considerations</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Ensure accurate cell counts at both time points</li>
                <li>Consider growth phase effects on doubling time</li>
                <li>Verify consistent growth conditions</li>
                <li>Check for potential contamination</li>
                <li>Use the growth curve to identify unusual patterns</li>
                <li>Consider sample size and timing effects</li>
                <li>Compare with standard growth rates for your cell type</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Cell Growth <span className="text-blue-600">📊</span></h2>
          <div className="text-gray-600">
            <p>
              Cell population growth typically follows an exponential pattern, characterized by:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Doubling Time:</strong> The time required for the cell population to double in size</li>
              <li><strong>Growth Rate:</strong> The rate at which the population increases over time</li>
              <li><strong>Exponential Phase:</strong> The period of rapid, logarithmic growth</li>
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CellPopulationDoublingCalculator;